import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { useIonRouter } from '@ionic/react';
import { getUserAuditReport, setSlackActivityNotify } from '../../services/profileService';
import { useProfileStore } from '../../store/useProfileStore';
import { useRefresh } from '../../contexts/RefreshContext';
import { formatDateWithTimezone } from '../../utils/dateUtils';
import {
  UserSortFilterModal,
  type UserSortField,
  type UserSortOrder,
} from './UserSortFilterModal';
import { SlackActivityToggle } from '../shared/SlackActivityToggle';
import type { UserAuditReportRow } from '../../types/userAudit';

const CERTIFICATION_LABELS: Record<UserAuditReportRow['certificationStatus'], string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  complete: 'Complete',
};

const ACCOUNT_STATUS_LABELS: Record<UserAuditReportRow['accountStatus'], string> = {
  active: 'Active',
  pending: 'Pending',
  deleted: 'Deleted',
  suspended: 'Suspended',
};

const ROW_GRID = 'grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_minmax(0,1.5fr)_11rem] gap-x-6 gap-y-2 items-center';

function RowColumnHeader() {
  return (
    <div className={`${ROW_GRID} px-4 py-2 border-b border-gray-100 bg-gray-50/60 hidden md:grid`}>
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">UUID</p>
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Display Name</p>
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Email</p>
      <span />
    </div>
  );
}

function AuditField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 flex-1 basis-[160px]">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</p>
      <div className="text-sm text-gray-900 mt-1 break-words">{children}</div>
    </div>
  );
}

function AuditSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50/80 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">{title}</p>
      <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
        {children}
      </div>
    </div>
  );
}

function formatRoles(roles: string[]): string {
  if (!roles.length) return '—';
  return roles.join(', ');
}

type SortField = UserSortField;
type SortOrder = UserSortOrder;

function matchesUserSearch(user: UserAuditReportRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    user.displayName,
    user.email,
    user.id,
    user.clientName,
    user.teamName,
    ...user.roles,
  ].some((value) => value.toLowerCase().includes(q));
}

function sortUsers(
  rows: UserAuditReportRow[],
  field: SortField,
  order: SortOrder,
): UserAuditReportRow[] {
  const sorted = [...rows].sort((a, b) => {
    const aVal = (a[field] ?? '').toLowerCase();
    const bVal = (b[field] ?? '').toLowerCase();
    return aVal.localeCompare(bVal);
  });
  return order === 'desc' ? sorted.reverse() : sorted;
}

function AuditUserRow({
  user,
  timezone,
  expanded,
  onToggle,
  onToggleSlackActivity,
}: {
  user: UserAuditReportRow;
  timezone?: string;
  expanded: boolean;
  onToggle: () => void;
  onToggleSlackActivity: () => void;
}) {
  const router = useIonRouter();

  return (
    <div className={`border-b border-gray-100 last:border-b-0 ${expanded ? 'bg-gray-50/50' : ''}`}>
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        className={`${ROW_GRID} px-4 py-3 cursor-pointer transition-colors ${expanded ? '' : 'hover:bg-gray-50'}`}
      >
        <div className="min-w-0">
          <p className="text-xs text-gray-400 md:hidden">UUID</p>
          <p className="text-sm font-mono text-gray-900 md:mt-0 mt-0.5 break-all">{user.id}</p>
        </div>
        <div className="min-w-0">
          <p className="text-xs text-gray-400 md:hidden">Display Name</p>
          <p className="text-sm font-medium text-gray-900 md:mt-0 mt-0.5 break-words">{user.displayName}</p>
        </div>
        <div className="min-w-0">
          <p className="text-xs text-gray-400 md:hidden">Email</p>
          <p className="text-sm text-gray-900 md:mt-0 mt-0.5 break-words">{user.email}</p>
        </div>

        <div className="flex items-center justify-end gap-2 shrink-0">
          {user.clientId ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                router.push(`/client-detail?clientId=${user.clientId}`, 'none');
              }}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-colors truncate max-w-[8.5rem]"
              title={user.clientName}
            >
              {user.clientName}
            </button>
          ) : (
            <span className="text-sm text-gray-400 px-3 py-1.5">No client</span>
          )}
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-gray-100">
          <AuditSection title="Account">
            <AuditField label="Invite Status">
              {user.inviteStatus === 'pending'
                ? <span className="italic text-orange-500">Pending</span>
                : <span className="text-green-700">Accepted</span>}
            </AuditField>
            <AuditField label="Account Status">
              {user.accountStatus === 'pending'
                ? <span className="italic text-orange-500">{ACCOUNT_STATUS_LABELS[user.accountStatus]}</span>
                : user.accountStatus === 'suspended' || user.accountStatus === 'deleted'
                  ? <span className="text-red-600">{ACCOUNT_STATUS_LABELS[user.accountStatus]}</span>
                  : ACCOUNT_STATUS_LABELS[user.accountStatus]}
            </AuditField>
            <AuditField label="Roles">{formatRoles(user.roles)}</AuditField>
          </AuditSection>

          <AuditSection title="Assignment">
            <AuditField label="Client">{user.clientName}</AuditField>
            <AuditField label="Team">{user.teamName}</AuditField>
          </AuditSection>

          <AuditSection title="Learning & Activity">
            <AuditField label="Last Login">
              {user.lastLogin ? formatDateWithTimezone(user.lastLogin, timezone) : '—'}
            </AuditField>
            <AuditField label="Courses Completed">{user.coursesCompleted}</AuditField>
            <AuditField label="Certification %">
              {user.certificationPercent != null ? `${user.certificationPercent}%` : '—'}
            </AuditField>
            <AuditField label="Certification">
              {CERTIFICATION_LABELS[user.certificationStatus] ?? user.certificationStatus}
            </AuditField>
            <AuditField label="Slack Activity Tracking">
              <SlackActivityToggle
                checked={user.slackActivity}
                onChange={onToggleSlackActivity}
              />
            </AuditField>
          </AuditSection>
        </div>
      )}
    </div>
  );
}

export function UserAuditReport() {
  const { activeProfile } = useProfileStore();
  const [users, setUsers] = useState<UserAuditReportRow[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('displayName');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [showSortFilter, setShowSortFilter] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);

  const sortFilterActive = sortField !== 'displayName' || sortOrder !== 'asc';

  const displayedUsers = useMemo(() => {
    const filtered = users.filter((user) => matchesUserSearch(user, search));
    return sortUsers(filtered, sortField, sortOrder);
  }, [users, search, sortField, sortOrder]);

  const start = (page - 1) * pageSize;
  const pagedUsers = displayedUsers.slice(start, start + pageSize);
  const hasPrev = page > 1;
  const hasNext = start + pageSize < displayedUsers.length;

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await getUserAuditReport();
      setUsers(rows);
      setPage(1);
      setExpandedUserId(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  useEffect(() => {
    registerRefreshHandler('/users', fetch);
    return () => unregisterRefreshHandler('/users');
  }, [fetch, registerRefreshHandler, unregisterRefreshHandler]);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900 shrink-0">Users</h2>
          <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
                setExpandedUserId(null);
              }}
              placeholder="Search users..."
              className="flex-1 min-w-0 max-w-md px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={() => setShowSortFilter(true)}
              className={`p-2 rounded-lg transition-colors shrink-0 ${sortFilterActive ? 'text-blue-600 hover:text-blue-800 hover:bg-blue-50' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
              title="Sort"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill={sortFilterActive ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
            </button>
            <button
              onClick={fetch}
              className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
              title="Refresh"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 2v6h-6"/>
                <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
                <path d="M3 22v-6h6"/>
                <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="min-h-[120px]">
          {!loading && pagedUsers.length > 0 && <RowColumnHeader />}
          {loading ? (
            <div className="px-4 py-12 text-center text-gray-400">Loading...</div>
          ) : pagedUsers.length === 0 ? (
            <div className="px-4 py-12 text-center text-gray-400">No users found</div>
          ) : (
            pagedUsers.map((user) => (
              <AuditUserRow
                key={user.id}
                user={user}
                timezone={activeProfile?.timezone}
                expanded={expandedUserId === user.id}
                onToggle={() => setExpandedUserId((prev) => (prev === user.id ? null : user.id))}
                onToggleSlackActivity={async () => {
                  const enabled = !user.slackActivity;
                  setUsers((prev) => prev.map((u) => (
                    u.id === user.id ? { ...u, slackActivity: enabled } : u
                  )));
                  try {
                    await setSlackActivityNotify(user.id, enabled);
                  } catch {
                    await fetch();
                  }
                }}
              />
            ))
          )}
        </div>

        <div className="flex items-center justify-end gap-4 px-4 py-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Rows:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
                setExpandedUserId(null);
              }}
              className="text-sm border border-gray-200 rounded px-2 py-1"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setPage(page - 1); setExpandedUserId(null); }}
              disabled={!hasPrev}
              className={`text-sm px-3 py-1 rounded ${hasPrev ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'}`}
            >
              &larr; Previous
            </button>
            <span className="text-sm text-gray-500">Page {page}</span>
            <button
              onClick={() => { setPage(page + 1); setExpandedUserId(null); }}
              disabled={!hasNext}
              className={`text-sm px-3 py-1 rounded ${hasNext ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'}`}
            >
              Next &rarr;
            </button>
          </div>
        </div>
      </div>

      {showSortFilter && (
        <UserSortFilterModal
          sortField={sortField}
          sortOrder={sortOrder}
          onApply={(field, order) => {
            setSortField(field);
            setSortOrder(order);
            setPage(1);
            setExpandedUserId(null);
            setShowSortFilter(false);
          }}
          onClose={() => setShowSortFilter(false)}
        />
      )}
    </div>
  );
}
